import { useEffect, useState } from "react";
import { View, Text, Pressable, Linking } from "react-native";
import { ScreenContainer, ScrollableContainer } from "../../components";
import { ScreenProps } from "../types";
import { colors } from "../../../theme";
import React from "react";
import { Feather } from "@expo/vector-icons";
import { useQuery, gql } from "@apollo/client";
import { CURRENT_PERSON_FRAGMENT } from "../fragments";
import ItemCard from "../../components/ItemCard";
import { InvoiceLoader } from "../HomeScreen/SkeletonLoaders";
import { useFeatureFlags } from "../../hooks/useFlagr";
import PaymentOptionsModal from "./PaymentOptionsModal";
import { sortBy, orderBy } from "lodash";
import { TouchableRipple } from "react-native-paper";
import { openBrowser } from "../utils";

export const DUES_QUERY = gql`
  query DuesQuery {
    duesCycle: currentDuesCycle {
      id
      endsOn
      order {
        id
        description
        payable
      }
    }
  }
`;

interface DuesQuery {
  duesCycle: {
    order?: {
      id: string;
      payable: boolean;
      description: string;
    };
  };
}

export interface Order {
  id: string;
  balance: number;
  createdAt: string;
  description: string;
  dueDate: string;
  orderType: string;
  payable: boolean;
  status: string;
  total: number;
  type: string;
  pdfUrl?: string;
  isPastDue?: boolean;
  isCurrentDue?: boolean;
}

export const GET_ORDERS_QUERY = gql`
  query GetOrders {
    currentPerson {
      ...CurrentPersonFragment
      orders {
        id
        balance
        createdAt
        description
        dueDate
        orderType
        payable
        status
        total
        type
        pdfUrl
      }
    }
  }
  ${CURRENT_PERSON_FRAGMENT}
`;

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

const getOrderBadge = (order: Order) => {
  if (order.isPastDue) {
    return (
      <View className="flex-row items-center">
        <Feather name="alert-triangle" size={14} color={colors.red.dark} />
        <Text className="text-red-dark text-sm font-medium ml-1">Past Due</Text>
      </View>
    );
  }
  if (order.status === "canceled") {
    return (
      <View className="flex-row items-center">
        <Feather name="x-circle" size={14} color={colors.red.DEFAULT} className="mr-1" />
        <Text className="text-red-500 text-sm font-medium">Canceled</Text>
      </View>
    );
  }

  if (order.status === "paid") {
    return (
      <View className="flex-row items-center">
        <Feather name="check" size={16} color={colors.green.dark} className="mr-1" />
        <Text className="text-green-dark text-sm font-medium ml-1">Paid</Text>
      </View>
    );
  }

  return (
    <View className="flex-row items-center">
      <Feather name="alert-circle" size={14} color={colors.yellow.dark} />
      <Text className="text-yellow-dark text-sm font-medium ml-1">Payment Required</Text>
    </View>
  );
};

function PaneToggle({
  panes,
  selected,
  select,
}: {
  selected: string;
  panes: { id: string; slug: string; name: string; disabled?: boolean }[];
  select(pane: { slug: string }): void;
}) {
  return (
    <View className={`items-start px-6 py-6 md:py-4 md:mt-7 md:mb-2`}>
      <View className="flex-row rounded-lg bg-sky-lighter justify-center items-center mx-auto">
        {panes.map((pane) => (
          <TouchableRipple
            key={pane.id}
            testID="pane-toggle"
            onPress={() => select(pane)}
            disabled={pane.disabled}
            className={`rounded-lg py-2 px-4 border items-center ${
              selected === pane.slug ? "border-primary-light bg-sky-white" : "border-sky-lighter bg-transparent"
            } ${pane.disabled ? "bg-sky-lightest" : ""}`}
          >
            <Text
              className={`${
                selected === pane.slug ? "font-500 text-primary-light" : "font-400 text-ink-light"
              } text-small-tight`}
            >
              {pane.name}
            </Text>
          </TouchableRipple>
        ))}
      </View>
    </View>
  );
}

const InvoiceList = ({
  invoiceList,
  handleClick,
  slug,
}: {
  invoiceList: Order[];
  handleClick: (order: Order) => void;
  slug: string;
}) => {
  if (invoiceList.length === 0 && slug === "invoices") {
    return (
      <View className="flex-1 items-center justify-center py-8">
        <Feather name="check-circle" size={48} color={colors.green.DEFAULT} className="mb-4" />
        <Text className="text-regular-tight font-medium text-center text-gray-500 mb-2">You're all caught up!</Text>
        <Text className="text-regular-tight text-center text-gray-500">You have no outstanding invoices at this time.</Text>
      </View>
    );
  }
  return (
    <View>
      {invoiceList.map((order, index) => {
        return (
          <Pressable key={order.id} onPress={() => handleClick(order)}>
            <View
              className={`flex-col border-b border-sky-light py-4 px-6 lg:px-0 ${
                index === invoiceList.length - 1 ? "lg:border-b-0" : ""
              }`}
            >
              <View className="flex-row justify-between items-center">
                <View className="flex-1 pr-2">
                  <Text className="text-regular-tight font-700 text-ink-darkest" numberOfLines={1}>
                    {order.description || `Order ${order.id}`}
                  </Text>
                </View>
                <View className="flex-row items-center">
                  <Text className="text-regular-tight font-700 text-ink-darkest">${(order.total / 100).toFixed(2)}</Text>
                  {order.payable && order.balance > 0 && (
                    <View className="-mr-2">
                      <Feather name="chevron-right" size={24} color={colors.ink.darkest} />
                    </View>
                  )}
                </View>
              </View>
              <View className="flex-row justify-between items-center mt-3">
                <Text className={`text-small-tight font-400  ${order.isPastDue ? "text-red-dark" : "text-ink-dark"}`}>
                  {order.dueDate ? `Due: ${formatDate(order.dueDate)}` : ""}
                </Text>
                {getOrderBadge(order)}
              </View>
            </View>
          </Pressable>
        );
      })}
    </View>
  );
};

const InvoiceListScreen = ({ route, navigation }: ScreenProps<"InvoiceList">) => {
  const { slug } = route.params;

  const { data, loading, error } = useQuery(GET_ORDERS_QUERY, {
    context: {
      callerWillHandleError: () => true,
    },
  });
  const flags = useFeatureFlags();
  const { data: duesData } = useQuery<DuesQuery>(DUES_QUERY, {
    fetchPolicy: "cache-and-network",
  });
  const [paymentOptionsModalVisible, setPaymentOptionsModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const _orders: Order[] = data?.currentPerson?.orders || [];
  const cutoffDate = new Date("2023-12-31");
  const [invoiceList, setInvoiceList] = useState<Order[]>([]);

  const _outstandingOrders = _orders.filter(
    (order) => order.payable && order.balance > 0 && new Date(order.createdAt) > cutoffDate
  );
  const orders = _orders.filter((order) => new Date(order.createdAt) > cutoffDate);

  useEffect(() => {
    if (slug === "invoices") {
      const outstandingOrders = orders.filter((order) => order.payable && order.balance > 0);
      setInvoiceList(
        sortBy(outstandingOrders, "dueDate").map((order) => ({
          ...order,
          isPastDue:
            order.id === duesData?.duesCycle?.order?.id && outstandingOrders.filter((o) => o.orderType === "dues").length > 1,
          isCurrentDue: order.orderType === "dues" && order.id !== duesData?.duesCycle?.order?.id,
        }))
      );
    } else {
      setInvoiceList(
        orderBy(
          orders.filter((order) => order.status === "paid"),
          "createdAt",
          "desc"
        )
      );
    }
  }, [slug, loading]);

  if (loading) {
    return (
      <ScreenContainer navigationTitle="Billing & Payments">
        <View className="p-4">
          <InvoiceLoader />
        </View>
      </ScreenContainer>
    );
  }

  if (error) {
    return (
      <ScreenContainer navigationTitle="Billing & Payments">
        <View className="flex-1 items-center justify-center p-4">
          <Feather name="alert-circle" size={24} color={colors.red.DEFAULT} className="mb-2" />
          <Text className="text-regular-tight text-center text-red-500 mb-2 mt-2">Unable to load your invoices</Text>
          <Text className="text-regular-tight text-center text-gray-500">
            Please try again later or contact support if the problem persists.
          </Text>
        </View>
      </ScreenContainer>
    );
  }

  const handleClick = async (order: Order) => {
    if (order.payable) {
      const paymentUrl = order.type === "ccms" ? `https://payments.pga.org?invoiceNumbers=${order.id}` : "/member/dues";
      if (paymentUrl === "/member/dues") {
        if (flags["membership-new-payment-flow"]) {
          if (order.orderType === "dues") {
            setSelectedOrder(order);
            setPaymentOptionsModalVisible(true);
          }
        } else {
          navigation.navigate("Dues", {
            ...(flags["dues-order-lookup"] && { orderId: order.id }),
            assignProxy: false,
          });
        }
      } else {
        try {
          const supported = await Linking.canOpenURL(paymentUrl);
          if (supported) {
            await Linking.openURL(paymentUrl);
          }
        } catch (error) {
          console.error("An error occurred:", error);
        }
      }
    } else {
      if (order.pdfUrl) {
        openBrowser(order.pdfUrl)();
      }
    }
  };

  const selectPane = (pane: { slug: string }) => {
    navigation.navigate("InvoiceList", { slug: pane.slug });
  };

  return (
    <ScreenContainer navigationTitle="Billing & Payments">
      <ScrollableContainer>
        {flags["membership-new-payment-flow"] ? (
          <View className="">
            <PaneToggle
              panes={[
                { id: "invoices", slug: "invoices", name: "Invoices" },
                { id: "receipts", slug: "receipts", name: "Receipts" },
              ]}
              selected={slug}
              select={selectPane}
            />
            <InvoiceList slug={slug} invoiceList={invoiceList} handleClick={handleClick} />
          </View>
        ) : (
          <View className="p-4">
            {_outstandingOrders.map((order) => (
              <ItemCard key={order.id} variant="default" onClick={() => handleClick(order)} badge={getOrderBadge(order)}>
                <ItemCard.Header>{formatDate(order.createdAt)}</ItemCard.Header>
                <ItemCard.Content>
                  <View className="flex-1">
                    <Text className="text-lg font-medium mb-2">{order.description || `Order ${order.id}`}</Text>
                  </View>
                  <View className="ml-auto">
                    {order.payable && (
                      <Pressable onPress={() => handleClick(order)}>
                        <Feather name="credit-card" size={20} color={colors.primary.DEFAULT} />
                      </Pressable>
                    )}
                  </View>
                </ItemCard.Content>
                <ItemCard.Footer>${(order.total / 100).toFixed(2)}</ItemCard.Footer>
              </ItemCard>
            ))}
          </View>
        )}
        <PaymentOptionsModal
          visible={paymentOptionsModalVisible}
          onDismiss={() => setPaymentOptionsModalVisible(false)}
          order={selectedOrder}
          pastDueOrderTitle={duesData?.duesCycle?.order?.description || ""}
        />
      </ScrollableContainer>
    </ScreenContainer>
  );
};

export default InvoiceListScreen;
